import { Box, Stack, Typography } from '@mui/material';
import { Role } from 'graphql/generated/graphqlTypes';
import useEditVisitorGroups from 'hooks/allVisitors/drawers/useEditVisitorGroups';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { isVisitorHasInstallerRole } from 'utils/Visitor/visitor';
import { isMaxNumberOfUsersReached, VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import Loader from 'v2/components/Loader/Loader';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { formatTranslation } from 'v2/utils/formatTranslation';
import { getVisitorOrigin, VisitorOrigin } from 'v2/utils/visitorOrigin';
import VisitorGroupsSection from '../components/VisitorGroupsSection';
import VisitorRolesSection from '../components/VisitorRolesSection';

interface Props {
  visitorId: string;
}

export interface EditVisitorGroupsFormValues {
  isAdministratorRole: boolean;
  isInstallerRole: boolean;
  selectedGroupIds: string[];
  expirationByGroupId: Record<string, string | null>;
}

const FORM_ID = 'edit-visitor-groups-form';

const EditVisitorGroupsDrawer = ({ visitorId }: Props) => {
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const [error, setError] = useState<string>();

  const {
    coContractors,
    coContractorsLoading,
    visitorDetails,
    visitorDetailsLoading,
    administratorsVisitorGroups,
    installersVisitorGroups,
    updateVisitorGroupsMembership
  } = useEditVisitorGroups({
    visitorId,
    handleFetchError: setError
  });

  const isAdministratorsGroupFull = (administratorsVisitorGroups ?? []).some(isMaxNumberOfUsersReached);
  const isInstallersGroupFull = (installersVisitorGroups ?? []).some(isMaxNumberOfUsersReached);

  const visitorOrigin = visitorDetails ? getVisitorOrigin(visitorDetails) : undefined;
  const isEntraIdVisitor = visitorOrigin === VisitorOrigin.EntraId;
  const isKeySharingVisitor = visitorOrigin === VisitorOrigin.KeySharing;
  const canEditRoles = !isKeySharingVisitor;

  // Memoised so the `values` reference stays stable across renders; otherwise react-hook-form re-syncs
  // on every render and resets fields updated via `setValue` (e.g. the per-group expiration date).
  const formValues = useMemo<EditVisitorGroupsFormValues | undefined>(() => {
    if (!visitorDetails) return undefined;
    const coContractorMemberships = visitorDetails.visitorGroupMembership.filter(
      (membership) =>
        membership.visitorGroup.type !== VisitorGroupType.Installers &&
        membership.visitorGroup.type !== VisitorGroupType.Administrators
    );
    return {
      isAdministratorRole: visitorDetails.isAdmin,
      isInstallerRole: isVisitorHasInstallerRole(visitorDetails),
      selectedGroupIds: coContractorMemberships.map((membership) => membership.visitorGroup.id),
      expirationByGroupId: Object.fromEntries(
        coContractorMemberships.map((membership) => [membership.visitorGroup.id, membership.expirationDate ?? null])
      )
    };
  }, [visitorDetails]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting }
  } = useForm<EditVisitorGroupsFormValues>({
    defaultValues: {
      isAdministratorRole: false,
      isInstallerRole: false,
      selectedGroupIds: [],
      expirationByGroupId: {}
    },
    values: formValues
  });

  const isLoading = coContractorsLoading || visitorDetailsLoading;

  const onSubmit = async (data: EditVisitorGroupsFormValues) => {
    setError(undefined);
    try {
      const result = await updateVisitorGroupsMembership(
        data.selectedGroupIds.map((visitorGroupId) => ({
          role: Role.None,
          visitorGroupId,
          expirationDate: data.expirationByGroupId[visitorGroupId]
        })),
        data.isAdministratorRole,
        data.isInstallerRole
      );
      if (result?.data) {
        showSnackbar(translation.saved_changes, { severity: 'success' });
        closeDrawer();
      }
    } catch {
      // The mutation's onError already shows the message; keep the drawer open without an unhandled rejection.
    }
  };

  return (
    <DrawerTemplate
      loading={isSubmitting}
      title={translation.edit_group}
      primaryAction={{
        label: translation.save,
        onClick: handleSubmit(onSubmit),
        loading: isLoading || isSubmitting,
        form: FORM_ID,
        id: 'save-button'
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-button' }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Loader />
        </Box>
      ) : (
        <Stack id={FORM_ID} component="form" onSubmit={handleSubmit(onSubmit)} noValidate gap={2}>
          {!isEntraIdVisitor && (
            <Typography variant="body2" color="textSecondary">
              {formatTranslation(translation.select_user_group_membership, { name: visitorDetails?.name ?? '' })}
            </Typography>
          )}
          {canEditRoles && (
            <VisitorRolesSection
              control={control}
              canEditAdministratorRole={!isEntraIdVisitor}
              isAdministratorsGroupFull={isAdministratorsGroupFull}
              isInstallersGroupFull={isInstallersGroupFull}
            />
          )}
          {visitorDetails && (
            <VisitorGroupsSection
              control={control}
              setValue={setValue}
              coContractors={coContractors}
              visitor={visitorDetails}
            />
          )}
        </Stack>
      )}
    </DrawerTemplate>
  );
};

export default EditVisitorGroupsDrawer;
